import { LogManager } from '@src/utility/Logger';
import { type IReactionDisposer, reaction } from 'mobx';

const logger = LogManager.getLogger('AdaptivePlaybackSourceManager');

/**
 * Narrow getter interface so the manager stays decoupled from
 * VideoCreatorStore / ClipVideoCreatorAdapter specifics.
 */
export interface AdaptivePlaybackSourceHost {
  /** Current playhead time (observable in the host). */
  getTime(): number;
  /** Whether the renderer is currently playing (observable in the host). */
  getIsPlaying(): boolean;
  /** Whether the user is actively scrubbing the timeline. */
  getIsScrubbing(): boolean;
  /** Checks whether the current time is outside the cached split-window. */
  getNeedsRefresh(time: number): boolean;
  /** Triggers a timeline recalculation (refreshTimeline on the editor store). */
  refreshTimeline(): void;
}

const PLAYBACK_REFRESH_INTERVAL = 500; // ms between refreshes for adaptive/windowed playback
const SCRUBBING_DEBOUNCE_MS = 150; // ms debounce for scrubbing window updates

/**
 * Manages MobX reactions that keep the hybrid-adaptive layout and timeline
 * windowing in sync with the playhead position:
 *
 *  1. **Time reaction** – fires on seek (not during playback or scrubbing).
 *     Debounced at 300 ms so rapid seeking doesn't cause excessive recalcs.
 *
 *  2. **Playback interval** – polls every 500 ms while the renderer is playing
 *     and fires `refreshTimeline` when the window needs updating.
 *
 *  3. **Scrubbing reaction** – fires while the user drags the playhead.
 *     Debounced at 150 ms; skips if the playhead is still inside the current
 *     window. When scrubbing ends, fires one final playback-mode refresh so
 *     the full forward window is loaded.
 *
 * All reactions are disposed when `dispose()` is called.
 */
export class AdaptivePlaybackSourceManager {
  private timeReactionDisposer?: IReactionDisposer;
  private playingReactionDisposer?: IReactionDisposer;
  private scrubbingReactionDisposer?: IReactionDisposer;
  private scrubbingEndReactionDisposer?: IReactionDisposer;
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private scrubbingDebounceTimer: ReturnType<typeof setTimeout> | null = null;
  private playbackInterval: ReturnType<typeof setInterval> | null = null;

  constructor(private host: AdaptivePlaybackSourceHost) {
    this.setupTimeReaction();
    this.setupPlayingReaction();
    this.setupScrubbingReaction();
    this.setupScrubbingEndReaction();
    logger.debug('AdaptivePlaybackSourceManager initialized');
  }

  // ------------------------------------------------------------------
  // Time reaction: seek / scrub (debounced, skipped during playback)
  // ------------------------------------------------------------------
  private setupTimeReaction(): void {
    this.timeReactionDisposer = reaction(
      () => this.host.getTime(),
      () => {
        if (this.host.getIsPlaying() || this.host.getIsScrubbing()) return;
        if (!this.host.getNeedsRefresh(this.host.getTime())) return;

        if (this.debounceTimer) {
          clearTimeout(this.debounceTimer);
        }
        this.debounceTimer = setTimeout(() => {
          this.debounceTimer = null;
          if (this.host.getIsScrubbing()) return;
          this.refresh('seek');
        }, 300);
      },
    );
  }

  // ------------------------------------------------------------------
  // Playing reaction: 1 s interval while playing, final check on stop
  // ------------------------------------------------------------------
  private setupPlayingReaction(): void {
    this.playingReactionDisposer = reaction(
      () => this.host.getIsPlaying(),
      playing => {
        if (playing) {
          this.playbackInterval = setInterval(() => {
            if (!this.host.getNeedsRefresh(this.host.getTime())) return;
            this.refresh('playback-interval');
          }, PLAYBACK_REFRESH_INTERVAL);
        } else {
          this.clearPlaybackInterval();
          // Fire one final check when playback stops
          if (this.host.getNeedsRefresh(this.host.getTime())) {
            this.refresh('playback-stopped');
          }
        }
      },
    );
  }

  // ------------------------------------------------------------------
  // Scrubbing reaction: debounced window updates while dragging
  // ------------------------------------------------------------------
  private setupScrubbingReaction(): void {
    this.scrubbingReactionDisposer = reaction(
      () => this.host.getTime(),
      () => {
        if (!this.host.getIsScrubbing()) return;
        if (!this.host.getNeedsRefresh(this.host.getTime())) return;

        if (this.scrubbingDebounceTimer) {
          clearTimeout(this.scrubbingDebounceTimer);
        }
        this.scrubbingDebounceTimer = setTimeout(() => {
          this.scrubbingDebounceTimer = null;
          if (!this.host.getIsScrubbing()) return;
          if (!this.host.getNeedsRefresh(this.host.getTime())) return;
          this.refresh('scrubbing');
        }, SCRUBBING_DEBOUNCE_MS);
      },
    );
  }

  // ------------------------------------------------------------------
  // Scrubbing end: load full playback window when playhead is released
  // ------------------------------------------------------------------
  private setupScrubbingEndReaction(): void {
    this.scrubbingEndReactionDisposer = reaction(
      () => this.host.getIsScrubbing(),
      scrubbing => {
        if (scrubbing) return;
        // Playhead released — always refresh to load the full forward window
        if (this.scrubbingDebounceTimer) {
          clearTimeout(this.scrubbingDebounceTimer);
          this.scrubbingDebounceTimer = null;
        }
        this.refresh('scrubbing-end');
      },
    );
  }

  private refresh(reason: string): void {
    logger.debug(
      '[hybrid-adaptive] playhead crossed split-window, refreshing',
      {
        currentTime: this.host.getTime(),
        reason,
      },
    );
    this.host.refreshTimeline();
  }

  private clearPlaybackInterval(): void {
    if (this.playbackInterval) {
      clearInterval(this.playbackInterval);
      this.playbackInterval = null;
    }
  }

  dispose(): void {
    this.timeReactionDisposer?.();
    this.playingReactionDisposer?.();
    this.scrubbingReactionDisposer?.();
    this.scrubbingEndReactionDisposer?.();

    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
    if (this.scrubbingDebounceTimer) {
      clearTimeout(this.scrubbingDebounceTimer);
      this.scrubbingDebounceTimer = null;
    }
    this.clearPlaybackInterval();

    logger.debug('AdaptivePlaybackSourceManager disposed');
  }
}
